import React, { useEffect, useState, useRef, useContext } from 'react';
import { useParams } from 'react-router-dom';

import { Tldraw, createTLStore, loadSnapshot, useEditor, InstancePresenceRecordType, DefaultMainMenu, TldrawUiMenuGroup, TldrawUiMenuItem, DefaultMainMenuContent } from 'tldraw'
import 'tldraw/tldraw.css'

import { API_URL } from './config';
import { connectToRoom, sendMessage } from './websockets';

import { UserContext } from './components/TokenAuthenticator';

import { useNavigate } from 'react-router-dom';
import callApi from './utils/callApi';

const SYNCHRO_COOLDOWN = 15000;

let saveTimeout = null;

function CustomMainMenu() {
  const navigate = useNavigate();
	return (
		<DefaultMainMenu>
			<div>
				<TldrawUiMenuGroup id="example">
					<TldrawUiMenuItem
						id="dashboard"
						label="🏠 My whiteboards"
						icon="external-link"
						readonlyOk
						onSelect={() => {
							navigate('/dashboard');
						}}
					/>
				</TldrawUiMenuGroup>
			</div>
			<DefaultMainMenuContent />
		</DefaultMainMenu>
	)
}

const getRemoteSnapshot = async (id, secret) => {
  const headers = {
    'X-Secret': secret,
  };

  // fetch from /api/whiteboard/:id
  const response = await callApi(`whiteboard/${id}`, 'GET', { headers, raw: true });

  // if 404 not found, create a new whiteboard
  if (response.status === 404 || response.status === 401)
    return response.status;

  try {
    const data = await response.json();

    if (data?.snapshot?.snapshot)
      return data;

  } catch (error) {
    console.error('Error fetching remote snapshot:', error);
  }

  return {};
}

function AutoSave({ id, token, uuid, username, secret, socket }) {
  const editor = useEditor();

  const saveSnapshot = async () => {
    const snapshot = editor.getSnapshot();

    await callApi(`whiteboard/${id}/snapshot`, 'POST', {
      body: { snapshot },
      headers: {
        'X-Secret': secret,
        Authorization: `Bearer ${token}`,
      },
    });
  }

  const handleChangeEvent = ({ changes }) => {
    const { added, removed, updated } = changes;

    // broadcast changes to all clients
    sendMessage(socket, uuid, username, {
      type: 'changes',
      changes: changes,
    });

    const modifications = [...Object.keys(added), ...Object.keys(removed), ...Object.keys(updated)];

    const filteredModifications = modifications.filter(modification => ![
      'pointer:pointer',
      'instance:instance',
      'page:page',
      'instance_page_state:page:page',
      'camera:page:page',
    ].includes(modification));

    if (!filteredModifications.length) return;

    // throttle saveSnapshot to SYNCHRO_COOLDOWN seconds
    clearTimeout(saveTimeout);
    saveTimeout = setTimeout(saveSnapshot, SYNCHRO_COOLDOWN);
  }

  useEffect(() => {
    const cleanupFn = editor.store.listen(handleChangeEvent, { source: 'user', scope: 'all' });

    return () => {
      cleanupFn();
    }
  }, [editor]);

  return null;
}

function UsersIndicator({ users, connectedClients }) {

  if (connectedClients <= 1) return null;

  // console.log('>>> putain users', users);

  return <div style={{ backgroundColor: 'white', padding: '5px', borderRadius: '5px', display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ fontSize: '12px', marginRight: '5px' }}>{connectedClients} connected</div>
    {users.map(user => <div key={`${user.uuid}-${user.username}`} style={{ backgroundColor: 'black', color: 'white', padding: '5px', borderRadius: '5px' }}>{user.username || user.uuid}</div>)}
  </div>;
}

function Editor({ reader = false }) {
  const navigate = useNavigate();
  const { token } = useContext(UserContext);

  const { id } = useParams();
  const [uuid] = useState(Math.random().toString(36).substring(2, 15));
  const [secret, setSecret] = useState(null);
  const [socket, setSocket] = useState(null);
  const editorRef = useRef(null);
  const isReadOnlyRef = useRef(false);
  const restoreReadOnlyTimeoutRef = useRef(null);
  const [connectedClients, setConnectedClients] = useState(0);
  const [username, setUsername] = useState(null);
  const [users, setUsers] = useState([]);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const passwordInputRef = useRef(null);

  const [whiteboard, setWhiteboard] = useState(null);

  // socket stuff
  useEffect(() => {
    if (!id) return;
    if (!whiteboard) return;

    const socket = connectToRoom(id, uuid, () => {
      console.log('socket opened');
    }, (data) => {
      if (data?.type === 'message') {
        console.log('socket message', data.message);
        return;
      }

      if (data?.type === 'connected') {
        setConnectedClients(data.count);
        return;
      }

      if (data?.type === 'list_users') {
        console.log('>>> list_users', data.users);
        setUsers(data.users);
        return;
      }

      if (data?.type === 'register') {
        console.log('>>> register', data);
        setUsers(prevUsers => {

          // look if the user is already in the list
          const userIndex = prevUsers.findIndex(user => user.uuid === data.uuid);
          if (userIndex === -1)
            return [...prevUsers, data];

          // otherwise update it with its username
          prevUsers[userIndex].username = data.username;

          return [...prevUsers];
        });

        return;
      }

      if (data?.type === 'leaving') {
        console.log('>>> leaving', data);
        setUsers(prevUsers => prevUsers.filter(user => user.uuid !== data.uuid));

        return;
      }

      // ignore messages from self
      if (data?.uuid === uuid) return;

      if (data?.type === 'changes' && editorRef.current) {
        editorRef.current.store.mergeRemoteChanges(() => {
          const { added, removed, updated } = data.changes;

          // console.log('changes', added, removed, updated);

          // Apply added changes
          Object.entries(added).forEach(([id, data]) => {
            if (id.startsWith('shape:')) {
              editorRef.current.createShapes([data]);
            }

            if (id.startsWith('binding:')) {
              editorRef.current.updateBindings([data]);
              editorRef.current.store.put([data]);
            }
          });

          // Apply removed changes
          Object.keys(removed).forEach((id) => {
            console.log('removing', id);
            editorRef.current.deleteShapes([id]);
          });

          // Apply updated changes
          Object.entries(updated).forEach(([id, updatedData]) => {

            if (id === 'pointer:pointer') {

              // console.log('updatedData', updatedData);
              // take the last updatedData
              const { x, y } = updatedData[updatedData.length - 1];

              // console.log('setting pointer', x, y);

              const peerPresence = InstancePresenceRecordType.create({
                id: InstancePresenceRecordType.createId(editorRef.current.store.id),
                currentPageId: editorRef.current.getCurrentPageId(),
                userId: data.uuid,
                userName: data.username || data.uuid,
                cursor: { x, y, type: 'default', rotation: 0 },
                lastActivityTimestamp: Date.now(),
              });

              const records = editorRef.current.store.allRecords();

              // console.log('records', records);
              let existingPresences = records.filter(({ typeName }) => typeName === 'instance_presence') || [];

              // console.log('existingPresences', existingPresences);

              // remove the existing presence record for this user if it exists
              existingPresences = existingPresences.filter(({ userId }) => userId !== data.uuid);

              // console.log('existingPresences', existingPresences);

              editorRef.current.store.put([...existingPresences, peerPresence]);

              return;
            }

            if (id.startsWith('shape:')) {
              const lastUpdatedData = updatedData[updatedData.length - 1];
              console.log('updating shape', id, lastUpdatedData);

              editorRef.current.createShapes([lastUpdatedData]);
            }

            if (id.startsWith('binding:')) {
              const lastUpdatedData = updatedData[updatedData.length - 1];
              console.log('updating binding', id, lastUpdatedData);

              editorRef.current.updateBindings([lastUpdatedData]);
              editorRef.current.store.put([lastUpdatedData]);
            }

            // below is only for reader
            if (!reader) return;

            if (id === 'camera:page:page') {
              const lastUpdatedData = updatedData[updatedData.length - 1];
              console.log('updating camera', id, lastUpdatedData);

              editorRef.current.setCamera(lastUpdatedData);
            }

            if (id === 'instance_page_state:page:page') {
              const lastUpdatedData = updatedData[updatedData.length - 1];
              console.log('updating instance_page_state', id, lastUpdatedData);

              // console.log('>> instance_page_state', lastUpdatedData);
            }

            if (id === 'instance:instance') {
              const lastUpdatedData = updatedData[updatedData.length - 1];
              console.log('updating instance', id, lastUpdatedData);

              editorRef.current.updateInstanceState(lastUpdatedData);
            }

            // if reader but not anymore in readonly mode, add layer over the canvas and hide all ui
            if (!isReadOnlyRef.current) {
              // set components
              const newComponents = { ...components };
              newComponents.InFrontOfTheCanvas = () => <div style={{ position: 'fixed', inset: 0, backgroundColor: 'transparent' }} />

              newComponents.Toolbar = null;
              newComponents.StylePanel = null;
              newComponents.ActionsMenu = null;
              newComponents.MainMenu = null;
              newComponents.QuickActions = null;

              setComponents(newComponents);
              isReadOnlyRef.current = true;
            }

            if (restoreReadOnlyTimeoutRef.current)
              clearTimeout(restoreReadOnlyTimeoutRef.current);

            restoreReadOnlyTimeoutRef.current = setTimeout(() => {
              isReadOnlyRef.current = false;
              setComponents({});
              editorRef.current.updateInstanceState({ isReadonly: true });
            }, 5000);

            console.log('updatedData', id);
          });
        });

        // console.log('changes', data.changes);

        return;
      }

      console.log('unsupported message type', data);
    });

    setSocket(socket);

    return () => {
      socket.close();
    };
  }, [id, whiteboard]);

  useEffect(() => {
    // retrieve list of users once when connected to the room
    if (!window._onceConnectedClients && connectedClients > 1) {
      window._onceConnectedClients = true;
      socket.send('list_users');
    }

    if (connectedClients > 1 && !username) {

      let newUsername = null;

      while (newUsername === null || newUsername.trim() === '')
        newUsername = window.prompt('Enter your name:');

      setUsername(newUsername);
      socket.send(`username::${newUsername}`);
    }
  }, [connectedClients]);

  useEffect(() => {
    if (username)
      sendMessage(socket, uuid, username, { type: 'register', username });
  }, [username]);

  const [storeWithStatus, setStoreWithStatus] = useState({
		status: 'loading',
	})

  const [components, setComponents] = useState({
    MainMenu: CustomMainMenu,
  });

	useEffect(() => {
		let cancelled = false

		async function loadRemoteSnapshot(id, secret) {
			const data = await getRemoteSnapshot(id, secret);

      if (data === 404 || data === 401) {
        navigate('/not-found');
        return;
      }

			if (cancelled) return

      if (data === 403 && !secret) {
        setIsPasswordModalOpen(true);
        return;
      }

      if (data === 403) {
        setPasswordError('Invalid password 😢');
        setIsPasswordModalOpen(true);
        return;
      }

      const snapshot = data?.snapshot?.snapshot || {};
      const whiteboard = data?.whiteboard || {};

      setWhiteboard(whiteboard);

			// Create the store
			const newStore = createTLStore()

			// Load the snapshot
			loadSnapshot(newStore, snapshot)

			// Update the store with status
			setStoreWithStatus({
				store: newStore,
				status: 'ready',
			})
		}

		loadRemoteSnapshot(id, secret)

		return () => {
			cancelled = true
		}
	}, [id, secret])

  const handlePasswordSubmit = async () => {
    setPasswordError('');
    const data = await getRemoteSnapshot(id, passwordInput);

    if (data === 401) {
      setPasswordError('Invalid password 😢');
    } else {
      setSecret(passwordInput);
      setIsPasswordModalOpen(false);
      setPasswordInput('');
    }
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handlePasswordSubmit();
    }
  };

  useEffect(() => {
    if (reader) {
      // put a div over the editor
      // components.InFrontOfTheCanvas = () => <div style={{ position: 'fixed', inset: 0, backgroundColor: 'transparent' }} />

      // hide ui
      // components.Toolbar = null;
      components.StylePanel = null;
      components.ActionsMenu = null;
      components.MainMenu = null;
      components.QuickActions = null;

      setComponents(components);
    }
  }, [reader]);

  useEffect(() => {
    if (isPasswordModalOpen && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [isPasswordModalOpen]);

  if (!whiteboard)
    return null;

  return <div style={{ position: 'relative', width: '100svw', height: '100svh' }}>
    <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
      <UsersIndicator users={users} connectedClients={connectedClients} />
    </div>

    <div style={{ position: 'fixed', inset: 0 }}>
      <Tldraw
        onMount={(editor) => {
          editorRef.current = editor;

          if (reader)
            editor.updateInstanceState({ isReadonly: true });

        }}
        onUIEvent={() => {
          console.log('eventUI');
        }}
        store={storeWithStatus}
        persistenceKey={`tldraw-${id}`}
        defaultPageName="Whiteboard"
        inferDarkMode={true}
        options={{
          maxPages: 1,
          showPages: false,
          disableMedia: true,
          hideUi: {
            media: true,
          },
          readOnly: reader,
        }}
        components={components}
      >
        {!reader && <AutoSave
          id={id}
          token={token}
          uuid={uuid}
          username={username}
          secret={secret}
          socket={socket}
        />}
      </Tldraw>
		</div>

    {isPasswordModalOpen && (
      <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    🔐 This whiteboard is protected
                  </h3>
                  <div className="mt-2">
                    <input
                      ref={passwordInputRef}
                      type="password"
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Password"
                    />
                    {passwordError && (
                      <p className="mt-2 text-sm text-red-600" id="password-error">
                        {passwordError}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handlePasswordSubmit}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Submit
              </button>
              <button
                type="button"
                onClick={() => {
                  navigate('/');
                }}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>;
}

export default Editor;
